import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { withLocalize } from 'react-localize-redux';
import PropTypes from 'prop-types';
import FormField from './FormField';
import css from './ExpandedDimension.css';
import ValueSourceSection from './ValueSourceSection';
import { Button, TextField } from '../../components/widgets';
import ToggleTabGroup from '../../components/atoms/ToggleTabGroup';
import AutoCompleteField from '../../components/molecules/AutoCompleteField';
import sharedCss from './styles.css';
import { improveDescription, updateCustomAIDimension } from '../services';
import toaster from '../../utils/toaster';
import { trackMixpanelEvent } from '../../utils/general';
import {
    VALUE_TYPES,
    FORM_FIELDS,
    CustomAIDimensionContext,
    getValueTypeOptions,
    MIXPANEL,
    FORM_TRANSLATIONS,
    buildMixpanelPayload,
    POPUP_DELAY,
    TOASTER_TRANSLATIONS,
} from '../consts';
import { IconVariants } from '../../components/foundations/Icon';

const APPS_LABEL = 'STATIC.PLACEHOLDERS.SELECT_APP';
const DESCRIPTION_CHAR_LIMIT = 2048;

const sortedStringify = arr => JSON.stringify([...(arr || [])].sort());

const hasFormChanged = (formData, initialData) =>
    formData.displayName !== initialData.displayName ||
    formData.description !== initialData.description ||
    formData.valueType !== initialData.valueType ||
    sortedStringify(formData.apps) !== sortedStringify(initialData.apps) ||
    sortedStringify(formData.options) !== sortedStringify(initialData.options);

const getFormValidation = (formData, hasChanges, isNewDimension) => {
    const isDescriptionLengthValid =
        formData.description?.trim() && formData.description.length <= DESCRIPTION_CHAR_LIMIT;
    const isAppsEmpty = formData.apps !== null && formData.apps.length === 0;
    const isDisplayNameEmpty = !formData.displayName?.trim();

    const canSave = isDescriptionLengthValid && !isAppsEmpty && !isDisplayNameEmpty && (isNewDimension || hasChanges);

    return canSave;
};

const buildDimensionPayload = (formData, dimensionId = null) => {
    const payload = {
        display_name: formData.displayName.trim(),
        description: formData.description.trim(),
        options: formData.options,
        apps: formData.apps,
        value_type: formData.valueType,
    };
    if (dimensionId) payload.id = dimensionId;
    return payload;
};

const ExpandedDimension = ({
    customAIDimension,
    originalDisplayName,
    isNewDimension,
    onUpdate,
    onCancel,
    onHasChangesChange,
    translate,
}) => {
    const { isReadOnly, apps, isAppsLoading } = useContext(CustomAIDimensionContext);
    const [isImproving, setIsImproving] = useState(false);
    const improveSnapshotRef = useRef({ original: null, improved: null });

    const valueTypeOptions = getValueTypeOptions(isReadOnly);

    const initialDataRef = useRef({
        displayName: originalDisplayName,
        description: customAIDimension.description,
        valueType: customAIDimension.value_type ?? VALUE_TYPES.SINGLE,
        options: customAIDimension.options || [],
        apps: customAIDimension.apps?.length ? customAIDimension.apps : null,
    });

    const [formData, setFormData] = useState(initialDataRef.current);

    useEffect(() => {
        setFormData(prev => ({ ...prev, displayName: customAIDimension.display_name || '' }));
    }, [customAIDimension.display_name]);

    const initialData = initialDataRef.current;
    const selectedAppsOptions = formData.apps === null ? apps : apps.filter(app => formData.apps?.includes(app.value));
    const selectedValueTypeOption =
        valueTypeOptions.find(option => option.value === formData.valueType)?.option || valueTypeOptions[0].option;

    const hasChanges = useMemo(() => hasFormChanged(formData, initialData), [formData, initialData]);

    useEffect(() => {
        onHasChangesChange(hasChanges);
    }, [hasChanges, onHasChangesChange]);

    const canSave = useMemo(() => getFormValidation(formData, hasChanges, isNewDimension), [
        formData,
        hasChanges,
        isNewDimension,
    ]);

    const shouldDisableImprove =
        isReadOnly ||
        isImproving ||
        !formData.description?.trim() ||
        (improveSnapshotRef.current.improved &&
            formData.description.trim() === improveSnapshotRef.current.improved.trim());

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const clearImproveSnapshot = () => {
        improveSnapshotRef.current = { original: null, improved: null };
    };

    const handleValueTypeToggle = option => {
        const selectedOption = valueTypeOptions.find(opt => opt.option === option);
        handleFieldChange(FORM_FIELDS.VALUE_TYPE, selectedOption?.value ?? VALUE_TYPES.SINGLE);
    };

    const handleAppsChange = selectedApps => {
        const appIds = selectedApps?.map(app => app.value);
        handleFieldChange(FORM_FIELDS.APPS, appIds.length === apps.length ? null : appIds);
    };

    const handleCancelEditing = () => {
        if (!isNewDimension) setFormData(initialData);
        clearImproveSnapshot();
        onCancel();
    };

    const trackDimensionEvent = (action, payload) => {
        trackMixpanelEvent(MIXPANEL.EVENT_PREFIX, action, buildMixpanelPayload(payload, apps));
    };

    const handleSave = async () => {
        const payload = buildDimensionPayload(formData, isNewDimension ? null : customAIDimension.id);

        if (isNewDimension) {
            onUpdate(payload);
            trackDimensionEvent(MIXPANEL.ACTIONS.SAVE_NEW, payload);
            return;
        }

        try {
            const updatedDimension = await updateCustomAIDimension(payload);
            onUpdate(updatedDimension);
            trackDimensionEvent(MIXPANEL.ACTIONS.EDIT, payload);
            setFormData({
                displayName: updatedDimension.display_name,
                description: updatedDimension.description,
                valueType: updatedDimension.value_type,
                options: updatedDimension.options || [],
                apps: updatedDimension.apps?.length ? updatedDimension.apps : null,
            });
            clearImproveSnapshot();
        } catch {
            toaster.error(translate(FORM_TRANSLATIONS.UPDATE_ERROR), { autoClose: POPUP_DELAY });
        }
    };

    const handleImprove = async () => {
        if (isReadOnly || !formData.displayName.trim() || !formData.description) {
            toaster.error(translate(TOASTER_TRANSLATIONS.IMPROVE_DESCRIPTION_MISSING_FIELDS), {
                autoClose: POPUP_DELAY,
            });
            return;
        }

        setIsImproving(true);

        try {
            const currentDescription = formData.description;
            const improvedDescription = await improveDescription(currentDescription, formData.displayName);
            improveSnapshotRef.current = { original: currentDescription, improved: improvedDescription };
            handleFieldChange(FORM_FIELDS.DESCRIPTION, improvedDescription);
        } catch (error) {
            clearImproveSnapshot();
            toaster.error(translate(TOASTER_TRANSLATIONS.IMPROVE_DESCRIPTION_ERROR), { autoClose: POPUP_DELAY });
        } finally {
            setIsImproving(false);
        }
    };

    const handleDescriptionChange = value => {
        handleFieldChange(FORM_FIELDS.DESCRIPTION, value);
        if (improveSnapshotRef.current.original && improveSnapshotRef.current.original.trim() !== value.trim()) {
            clearImproveSnapshot();
        }
    };

    const handleRevert = () => {
        if (!improveSnapshotRef.current.original) return;

        handleFieldChange(FORM_FIELDS.DESCRIPTION, improveSnapshotRef.current.original);
        clearImproveSnapshot();
    };

    return (
        <div className={css.container}>
            <div className={css.section}>
                <span className={css.sectionTitle}>Configuration</span>
                <FormField
                    title={FORM_TRANSLATIONS.DESCRIPTION_TITLE}
                    required
                    subTitle={FORM_TRANSLATIONS.DESCRIPTION_SUBTITLE}
                >
                    <div className={css.description}>
                        <TextField
                            value={formData.description}
                            onChange={handleDescriptionChange}
                            type="textarea"
                            placeholder={FORM_TRANSLATIONS.DESCRIPTION_PLACEHOLDER}
                            inputConfig={{ rows: 4 }}
                            inputClassName={sharedCss.textAreaField}
                            showCharCount
                            containerClass={css.descriptionTextArea}
                            maxLengthForCounter={DESCRIPTION_CHAR_LIMIT}
                            disabled={isReadOnly || isImproving}
                        />
                        {!isReadOnly && (
                            <div className={css.descriptionActions}>
                                <Button
                                    text={FORM_TRANSLATIONS.IMPROVE_BUTTON}
                                    onClick={handleImprove}
                                    type="link"
                                    Icon={!isImproving && 'ai-line'}
                                    iconVariant={shouldDisableImprove ? IconVariants.LIGHT : IconVariants.ACTIVE}
                                    className={css.descriptionAction}
                                    disabled={shouldDisableImprove}
                                    showSpinner={isImproving}
                                />
                                {improveSnapshotRef.current.original && !isImproving && (
                                    <Button
                                        text={FORM_TRANSLATIONS.REVERT_BUTTON}
                                        onClick={handleRevert}
                                        type="link"
                                        Icon="arrow-back-up"
                                        iconVariant={IconVariants.ACTIVE}
                                        disabled={isImproving}
                                        className={css.descriptionAction}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </FormField>
                <FormField
                    title={FORM_TRANSLATIONS.RESULT_TYPE_TITLE}
                    tooltip={FORM_TRANSLATIONS.RESULT_TYPE_TOOLTIP}
                    subTitle={FORM_TRANSLATIONS.RESULT_TYPE_SUBTITLE}
                >
                    <ToggleTabGroup
                        options={valueTypeOptions}
                        selectedOption={selectedValueTypeOption}
                        onToggle={handleValueTypeToggle}
                    />
                </FormField>
                <FormField title={FORM_TRANSLATIONS.APPS_TITLE} required subTitle={FORM_TRANSLATIONS.APPS_SUBTITLE}>
                    <div className={css.appsContainer}>
                        <AutoCompleteField
                            options={apps}
                            disableOptions={isReadOnly}
                            value={selectedAppsOptions}
                            onChange={handleAppsChange}
                            placeholder={FORM_TRANSLATIONS.APPS_PLACEHOLDER}
                            label={APPS_LABEL}
                            isMulti
                            advanced
                            loading={isAppsLoading}
                        />
                    </div>
                </FormField>
            </div>

            <div className={css.section}>
                <ValueSourceSection
                    values={formData.options}
                    onChange={value => handleFieldChange(FORM_FIELDS.OPTIONS, value)}
                />
                <div className={css.updateDimensionActions}>
                    <Button text={FORM_TRANSLATIONS.CANCEL_BUTTON} type="secondary" onClick={handleCancelEditing} />
                    {!isReadOnly && (
                        <Button
                            text={isNewDimension ? FORM_TRANSLATIONS.CREATE_BUTTON : FORM_TRANSLATIONS.UPDATE_BUTTON}
                            type="primary"
                            onClick={handleSave}
                            disabled={!canSave}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

ExpandedDimension.propTypes = {
    customAIDimension: PropTypes.shape({
        id: PropTypes.string,
        apps: PropTypes.arrayOf(PropTypes.number),
        description: PropTypes.string,
        display_name: PropTypes.string,
        value_type: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    originalDisplayName: PropTypes.string,
    isNewDimension: PropTypes.bool,
    onUpdate: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onHasChangesChange: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(ExpandedDimension);
